import axios from 'axios';

// Production API URL (hardcoded fallback)
const PRODUCTION_API_URL = 'https://portfolioallocationapp-production.up.railway.app/api';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If explicitly set in environment, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000/api';
  }

  // Local network testing (mobile on same WiFi) - IP addresses like 192.168.x.x
  if (/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname)) {
    return `http://${hostname}:8000/api`;
  }

  // Production fallback - use hardcoded production URL
  return PRODUCTION_API_URL;
};

const API_BASE_URL = getApiBaseUrl();

// Server error tracking for Einstein fallback
let consecutiveServerFailures = 0;
const SERVER_FAILURE_THRESHOLD = 3;
let serverErrorCallbacks = [];

// Subscribe to server down events
export const onServerDown = (callback) => {
  serverErrorCallbacks.push(callback);
  return () => {
    serverErrorCallbacks = serverErrorCallbacks.filter(cb => cb !== callback);
  };
};

// Check if error is a server-side failure (5xx or network error)
const isServerError = (error) => {
  if (!error.response && error.request) {
    // Network error - no response received
    return true;
  }
  if (error.response && error.response.status >= 500) {
    // Server error (5xx)
    return true;
  }
  return false;
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second default timeout
});

// Request interceptor for adding auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sessionToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and server down detection
axiosInstance.interceptors.response.use(
  (response) => {
    // Success - reset failure counter
    consecutiveServerFailures = 0;
    return response;
  },
  (error) => {
    if (isServerError(error)) {
      consecutiveServerFailures++;
      console.error(`Server error detected (${consecutiveServerFailures}/${SERVER_FAILURE_THRESHOLD}):`,
        error.response?.status || 'Network Error');

      // Trigger server down state after threshold
      if (consecutiveServerFailures >= SERVER_FAILURE_THRESHOLD) {
        console.error('Server appears to be down - triggering Einstein fallback');
        serverErrorCallbacks.forEach(callback => callback());
      }
    }

    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Fund Management
  getAllFunds: (limit = 100, offset = 0) => 
    axiosInstance.get(`/funds?limit=${limit}&offset=${offset}`),
  
  searchFunds: (query, limit = 20, signal) =>
    axiosInstance.post('/funds/search', { query, limit }, { signal }),

  // Unified search across all data sources (MFAPI + Tiingo)
  searchSecurities: (query, searchType = 'all') =>
    axiosInstance.post(`/data/search?query=${encodeURIComponent(query)}&search_type=${searchType}`),

  // Stock/ETF data (Tiingo)
  getStockData: (symbols, period = '1y') =>
    axiosInstance.get(`/data/stock/${symbols}?period=${period}`, { timeout: 90000 }),

  // Integrated optimization (mixed MFs and stocks)
  optimizeIntegratedPortfolio: (securities, objective = 'sharpe', years = 5, useAi = false) =>
    axiosInstance.post(`/optimize/integrated?objective=${objective}&years=${years}&use_ai=${useAi}`, securities, { timeout: 120000 }),

  saveSelectedFunds: (schemeCodes) =>
    axiosInstance.post('/funds/selected', { scheme_codes: schemeCodes }),

  // NAV Data - longer timeout for fetching multiple funds
  getNavData: (fundCodes, useCache = true) =>
    axiosInstance.get(`/nav/${fundCodes}?use_cache=${useCache}`, { timeout: 90000 }), // 90 seconds

  processNavData: (schemeCodes) =>
    axiosInstance.post('/nav/process', { scheme_codes: schemeCodes }, { timeout: 90000 }),

  // Portfolio Optimization - can take time for complex calculations
  optimizePortfolio: (params) =>
    axiosInstance.post('/optimize', params, { timeout: 90000 }), // 90 seconds

  getEfficientFrontier: (fundCodes, nPoints = 50) =>
    axiosInstance.get(`/optimize/efficient-frontier?fund_codes=${fundCodes}&n_points=${nPoints}`, { timeout: 120000 }), // 2 minutes

  // Cache Management
  getCacheStatus: () => 
    axiosInstance.get('/cache/status'),
  
  clearCache: () => 
    axiosInstance.post('/cache/clear'),

  // Health Check
  healthCheck: () => 
    axiosInstance.get('/health'),

  // Scanner/OCR APIs - longer timeout for file upload and processing
  uploadScan: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosInstance.post('/scan/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for upload + OCR processing
    });
  },

  processScan: (scanId) =>
    axiosInstance.post(`/scan/process/${scanId}`, {}, { timeout: 120000 }), // 2 minutes for OCR processing

  getScanResults: (scanId) =>
    axiosInstance.get(`/scan/results/${scanId}`),

  // Client-side OCR support - match funds from extracted text
  // Longer timeout for mobile devices where OCR can be slow
  matchFundsFromText: (extractedText, fundNames = [], confidence = 0) =>
    axiosInstance.post('/scan/match-text', {
      extracted_text: extractedText,
      fund_names: fundNames,
      confidence: confidence
    }, { timeout: 180000 }), // 3 minutes

  confirmExtractedFunds: (scanId, confirmedFunds) =>
    axiosInstance.post('/scan/confirm', {
      scan_id: scanId,
      confirmed_funds: confirmedFunds
    }),

  getScanSessions: () =>
    axiosInstance.get('/scan/sessions'),

  deleteScanSession: (scanId) =>
    axiosInstance.delete(`/scan/session/${scanId}`),

  // PDF Report Export
  exportPDF: (results) =>
    axiosInstance.post('/export/pdf', results, {
      responseType: 'blob',
    }),

  getAllReports: () =>
    axiosInstance.get('/reports'),

  getReportById: (clientId) =>
    axiosInstance.get(`/reports/${clientId}`),

  downloadReport: (clientId) =>
    axiosInstance.get(`/reports/${clientId}/download`, {
      responseType: 'blob',
    }),

  deleteReport: (clientId) =>
    axiosInstance.delete(`/reports/${clientId}`),

  // Backtest - accepts portfolio weights for historical performance analysis
  // Longer timeout for backtest as it's a heavy operation
  runBacktest: (params) =>
    axiosInstance.post('/backtest', params, { timeout: 120000 }), // 2 minute timeout

  // Chat - AllocQ chatbot
  sendChatMessage: (message, portfolioData = null, backtestData = null, selectedFunds = null) =>
    axiosInstance.post('/chat', {
      message,
      portfolio_data: portfolioData,
      backtest_data: backtestData,
      selected_funds: selectedFunds,
    }, { timeout: 30000 }),

  getChatHistory: () =>
    axiosInstance.get('/chat/history'),

  clearChatHistory: () =>
    axiosInstance.delete('/chat/history'),
};

// Export additional utilities

export const exportPDFReport = async (results) => {
  try {
    const response = await api.exportPDF(results);

    // Get filename from Content-Disposition header or generate default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'portfolio_report.pdf';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    // Get client ID from custom header
    const clientId = response.headers['x-client-id'];

    // Create download link
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename, clientId };
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw error;
  }
};

export const downloadResults = (data, filename = 'portfolio-optimization.json') => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const formatCurrency = (amount, currency = '₹') => {
  return `${currency}${amount.toLocaleString('en-IN')}`;
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};